import PhoneInput from "react-phone-number-input"
import "react-phone-number-input/style.css"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { cn, parseAttributes } from "../../lib/utils"
import { useFormStore } from "../../stores/use-form-store"
import {
	PhoneNumberInputConfig,
	type Attribute,
	type FieldProps,
	type LEAD_DETAILS,
} from "../../types/form-types"
import { Description } from "./components/description"
import OTPInputField from "./components/otp-field"
import { useEffect, useState } from "react"
import { USER_STORAGE_NAME } from "@/lib/constants"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

const normalizePhoneNumber = (value: string | undefined): string => {
	if (!value) return ""
	if (value.startsWith("+")) return value
	return `+${value}`
}

export const PhoneNumber = ({ form, field, commonAttributes }: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes

	const { validateOTP, resendOTPTimer } = getPhoneNumberInputAttributes(
		field.attributes,
	)

	const { verifiedFields, shouldDisable, setLeadDetails } = useFormStore()
	const isVerified = verifiedFields.get(field.field_id) || false
	const { config, isPreview } = useFormStore()

	const [isInitialized, setIsInitialized] = useState(false)
	const [isTimerExpired, setIsTimerExpired] = useState(true)

	useEffect(() => {
		if (
			field.field_type === "TEL" &&
			field.attributes.find(attr => attr.name === "alias")?.value ===
				"mobile"
		) {
			const fieldValue = form.getValues(field.field_id)
			if (!fieldValue && config?.type === "FORM" && !isPreview) {
				const leadDetails = JSON.parse(
					localStorage.getItem(USER_STORAGE_NAME) || "{}",
				) as { state: { user: LEAD_DETAILS } } // please sync the type with student portal auth store
				if (!leadDetails) return
				const mobileNumber =
					(leadDetails?.state?.user?.country_code || "") +
					" " +
					(leadDetails?.state?.user?.phone || "")
				if (mobileNumber.trim() !== "") {
					const normalizedValue = normalizePhoneNumber(mobileNumber)
					form.setValue(field.field_id, normalizedValue)
				}
			}
			setLeadDetails({
				mobile_field_id: field.field_id,
			})
		}
	}, [])

	useEffect(() => {
		if (!isInitialized && form.getValues(field.field_id)) {
			const currentValue = form.getValues(field.field_id)
			const normalizedValue = normalizePhoneNumber(currentValue)

			if (normalizedValue !== currentValue) {
				form.setValue(field.field_id, normalizedValue)
			}
			setIsInitialized(true)
		} else if (!isInitialized) {
			setIsInitialized(true)
		}
	}, [field.field_id, form, isInitialized])

	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			name={field.field_id}
			control={form.control}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<div className="space-y-3">
							<PhoneInput
								// {...formField}
								disabled={
									isVerified ||
									shouldDisable ||
									!isTimerExpired
								}
								international
								defaultCountry="IN"
								className={cn(
									"df-phone-wrapper df-input df-input-base h-12 px-4 text-xs transition-all duration-200",
								)}
								countrySelectProps={{
									className: "df-country-select",
								}}
								value={formField.value || ""}
								onChange={value => {
									const normalizedValue =
										normalizePhoneNumber(value)
									formField.onChange(normalizedValue)
								}}
							/>

							{validateOTP && !isVerified && (
								<OTPInputField
									type="MOBILE"
									resendOTPTimer={resendOTPTimer ?? 120}
									form={form}
									field={field}
									phoneNumber={formField.value}
									setIsTimerExpired={setIsTimerExpired}
								/>
							)}
							{config?.type === "FORM" &&
								validateOTP &&
								isVerified && (
									<p className="text-green-500">verified</p>
								)}
						</div>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

export function getPhoneNumberInputAttributes(attributes: Attribute[]) {
	const props: {
		validateOTP?: boolean
		resendOTPTimer?: number
	} = {}

	const parsedAttributes = parseAttributes(attributes)
	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case PhoneNumberInputConfig.ValidateWithOTP:
				props.validateOTP = value as boolean
				break
			case PhoneNumberInputConfig.ResendOTPTimer:
				props.resendOTPTimer = value as number
				break
		}
	})

	return props
}
